/* eslint-disable max-len */
import classNames from 'classnames';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import LectureApi from '../../apis/LectureApi';
import { VideoPlayer } from '../../components';
import { COURSE_STATUS_COMPLETE, DATE_YMD, LECTURE_STATUS_OF_COURSE, SYSTEM_PATH } from '../../core/configs/constants';
import { useIsActiveTab } from '../../core/utils/tabs';
import LectureContent from './components/LectureContent';
import RelatedLectures from './components/RelatedLectures';
import NotFoundScreen from '../404/NotFoundScreen';
import { useStore } from '../../core/utils/hook';

function LectureDetailScreen() {
    const [lecture, setLecture] = useState({});
    const api = new LectureApi();

    const {
        lectureStore: { updateStatusReplay }
    } = useStore();

    const params = useParams();
    const { checkActiveTab, isActiveTab } = useIsActiveTab();
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const header = document.getElementById('header-site');
        if (!header) return;

        const updateHeight = () => {
            setHeaderHeight(header.offsetHeight);
        };

        // Follow initial height header
        updateHeight();

        // Follow resize header
        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(header);

        // Follow resize window
        window.addEventListener('resize', updateHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateHeight);
        };
    }, []);

    useEffect(() => {
        if (!checkActiveTab) {
            fetchData(params?.id);
        }
    }, [params?.id, checkActiveTab]);

    // Near the end of a video several fetchData run concurrently; responses are not
    // guaranteed to arrive in order (jitter/mobile networks), so a stale response
    // (statusUserCourse=1) can arrive AFTER a newer one (=2) and overwrite it, making the
    // 修了テスト/テストを受ける button disappear even though the backend is already =2.
    // Tag each request and only apply the response of the LATEST issued request.
    const fetchSeqRef = useRef(0);
    const fetchData = async (id) => {
        fetchSeqRef.current += 1;
        const seq = fetchSeqRef.current;
        try {
            const lecture = await api.getLectureDetail({ id });
            if (seq !== fetchSeqRef.current) return; // a newer request exists -> drop this stale response
            setLecture(lecture?.data?.data || null);
        } catch (error) {
            if (seq !== fetchSeqRef.current) return;
            setLecture(null);
            console.log('error: ', error);
        }
    };

    const handleRefresh = () => {
        fetchData(params?.id);
    }

    const onEndVideo = () => {
        if ([COURSE_STATUS_COMPLETE.USER_COURSE_NOT_DO_TEST, COURSE_STATUS_COMPLETE.USER_COURSE_APPLY_CERTIFICATE].includes(lecture?.statusUserCourse)) {
            updateStatusReplay(params?.id, true);
        }
    }

    const handleTimeUpdate = useCallback(
        async (timeView, refresh) => {
            if (params?.id) {
                const lectureId = params.id;
                try {
                    const res = await api.updateVideoTimeViewed({ timeView, lectureId });
                    if (refresh && res.status !== 2) {
                        fetchData(params?.id)
                    }
                } catch (error) {
                    setLecture(null);
                    console.log('error: ', error);
                }
            }
        },
        [params?.id]
    );

    const isM3u8 = useMemo(() => {
        return lecture?.video && lecture.video.toLowerCase().includes('m3u8');
    }, [lecture]);

    if (isActiveTab) {
        return <div className="pd-30">同時に複数のコンテンツが開いています。 連続視聴するには、関連するタブを閉じてページを更新してください。</div>;
    }

    if (!lecture) return <div style={{ height: '68vh' }}><NotFoundScreen/></div>;

    return (
        <div id="main" className="wrapper main-execute-completion main-view-lecture">
            <div className="bres-cus" style={{ position: 'sticky', zIndex: 50, top: `${headerHeight}px`, left: '0', width: '100%', backgroundColor: '#FFF'}}>
                <div className="container">
                    <ul>
                        <li>
                            <a href="/">
                                <img src="/images/icon-home.png" alt="" />
                            </a>
                        </li>
                        {/* selected theme name */}
                        <li className="dash">\</li>
                        <li>
                            <a href={SYSTEM_PATH.THEMES + `?themeId=${lecture.themeId}`}>{lecture.themeName}</a>
                        </li>
                        {/* selected course name */}
                        <li className="dash">\</li>
                        <li>
                            <a href={SYSTEM_PATH.COURSE + lecture.courseId}>{lecture.courseName}</a>
                        </li>
                        <li className="dash">\</li>
                        <li>{lecture.name}</li>
                    </ul>
                </div>
            </div>

            <div className="content-view-lecture">
                <div className="container">
                    <h2 className={classNames('title-primary', lecture?.isNew && 'icon-lecture-new')}>{lecture.name}</h2>
                    <p className="date">
                        <span>公開日: </span>
                        {moment(lecture.publicDate).format(DATE_YMD)}
                    </p>
                    {lecture.video && (
                        <div className="iframe-youtube">
                            {
                                !lecture?.isOldCourse ? 
                                    <VideoPlayer
                                        options={{
                                            poster: lecture?.thumbnail || '',
                                            sources: [
                                                {
                                                    src: lecture.video,
                                                    type: isM3u8 ? 'application/x-mpegURL' : 'video/mp4'
                                                }
                                            ]
                                        }}
                                        onTimeUpdate={handleTimeUpdate}
                                        lastTimeViewed={lecture?.lastTimeViewed}
                                        videoLength={lecture?.videoLength}
                                        hasCompletedView={lecture?.userLectureStatus === LECTURE_STATUS_OF_COURSE.USER_LECTURE_COMPLETE}
                                        // totalLecturesActive={lecture?.totalLecturesActive}
                                        isFinalLecture={lecture?.isFinalLecture}
                                        showCompleteText={!lecture?.completionDate && lecture?.statusUserCourse !== 2}
                                        linkGoTest={!lecture?.completionDate && lecture?.statusUserCourse === 2 && `/course/${lecture?.courseId}/completion-test`}
                                        onRefresh={handleRefresh}
                                        onEndVideo={onEndVideo}
                                    /> :
                                    <VideoPlayer
                                        options={{
                                            poster: lecture?.thumbnail || '',
                                            sources: [
                                                {
                                                    src: lecture.video,
                                                    type: isM3u8 ? 'application/x-mpegURL' : 'video/mp4'
                                                }
                                            ]
                                        }}
                                        disableForward={false}
                                    />
                            }
                            
                        </div>
                    )}
                    {/* lecturer Introduction */}
                    <LectureContent title="講師紹介" content={lecture.lecturerIntroduction} />

                    {/* content */}
                    <LectureContent title="コンテンツ内容" content={lecture.content} />
                </div>
            </div>

            {lecture.courseId && <RelatedLectures courseId={lecture.courseId} lectureId={params?.id} isOldCourse={lecture?.isOldCourse}/>}
        </div>
    );
}

export default observer(LectureDetailScreen);
